import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { RootState, AppDispatch } from '@/store/store'
import { resendVerification } from '@/store/slices/authSlice'
import { useCurrency } from '@/contexts/CurrencyContext'
// @ts-ignore - Unused import FiDollarSign
import { FiMail, FiAlertCircle, FiRefreshCw, FiPlus, FiMapPin, FiClock, FiTag, FiDollarSign, FiImage, FiCheck, FiTrash2, FiX } from 'react-icons/fi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { gigApi } from '@/services/api/gigs'
import { gigCategoryApi, GigCategory } from '@/services/api/gigCategories'
import { useSnackbarContext } from '@/components/ui/SnackbarProvider'
import { SERVICE_TYPES } from '@/data/serviceTypes'
import GigImageUpload from '@/components/ui/GigImageUpload'
import VerificationBanner from '@/components/verification/VerificationBanner'
import { accessLimitService, AccessLimitStatus } from '@/services/accessLimitService'
import SelectDropdown, { SelectOption } from '@/components/ui/SelectDropdown'
import { fetchAccessLimits } from '@/store/slices/subscriptionSlice'

const CreateGigPage: React.FC = () => {
    const { user, isEmailVerified, isLoading } = useSelector((state: RootState) => state.auth)
    const { accessLimits, isAccessLimitsLoading, accessLimitsFetchedAt } = useSelector((state: RootState) => state.subscription)
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const { showSuccess, showError } = useSnackbarContext()
    const { userCurrency, getCurrencyInfo } = useCurrency()

    // Get currency info for symbol display
    const userCurrencyInfo = getCurrencyInfo(userCurrency)
    const currencySymbol = userCurrencyInfo?.symbol || userCurrency

    const [isResending, setIsResending] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [locationValidationError, setLocationValidationError] = useState('')
    const [categories, setCategories] = useState<GigCategory[]>([])
    const [loadingCategories, setLoadingCategories] = useState(true)
    const [selectedCategory, setSelectedCategory] = useState<GigCategory | null>(null)
    const [gigLimitStatus, setGigLimitStatus] = useState<AccessLimitStatus | null>(null)
    const [isCheckingAccess, setIsCheckingAccess] = useState(false)
    const [draftSaveStatus, setDraftSaveStatus] = useState<'saving' | 'saved' | 'unsaved'>('unsaved')
    const [hasDraft, setHasDraft] = useState(false)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [fullscreenImage, setFullscreenImage] = useState<string | null>(null)
    const draftSaveTimeoutRef = useRef<NodeJS.Timeout>()

    // Prevent body scrolling when fullscreen modal is open
    useEffect(() => {
        if (fullscreenImage) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = ''
        }

        // Cleanup on unmount
        return () => {
            document.body.style.overflow = ''
        }
    }, [fullscreenImage])
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: '', // Will store the category ID
        categorySlug: '',
        subcategory: '',
        location: {
            location: '',
            serviceType: 'onsite' as 'remote' | 'onsite' | 'hybrid'
        },
        payment: {
            amount: '',
            type: 'fixed' as 'fixed' | 'hourly',
            currency: 'USD'
        },
        duration: {
            estimated: '',
            isFlexible: true
        },
        requirements: [] as string[],
        tags: [] as string[],
        urgency: 'medium' as 'low' | 'medium' | 'high',
        experience: 'intermediate' as 'entry' | 'intermediate' | 'expert',
        images: [] as string[],
        requirementsText: '', // For the textarea input
        requireSubmission: true
    })

    // Load draft from localStorage on component mount
    useEffect(() => {
        const loadDraft = () => {
            try {
                const draftData = localStorage.getItem('gig_draft_form')
                if (draftData) {
                    const parsed = JSON.parse(draftData)
                    // Validate that the parsed data has the expected structure
                    if (parsed && typeof parsed === 'object' && parsed.title !== undefined) {
                        setFormData(prev => ({ ...prev, ...parsed }))
                        setHasDraft(true)
                    } else {
                        // Invalid draft data, remove it
                        localStorage.removeItem('gig_draft_form')
                    }
                }
            } catch (error) {
                console.error('Error loading draft:', error)
                // Remove corrupted draft data
                localStorage.removeItem('gig_draft_form')
            }
        }

        loadDraft()
    }, []) // Only run on mount

    // Save draft on page unload to ensure persistence
    useEffect(() => {
        const handleBeforeUnload = () => {
            try {
                localStorage.setItem('gig_draft_form', JSON.stringify(formData))
            } catch (error) {
                console.error('Error saving draft on unload:', error)
            }
        }

        window.addEventListener('beforeunload', handleBeforeUnload)
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }
    }, [formData])

    // Set selected category when categories are loaded and we have a category ID
    useEffect(() => {
        if (categories.length > 0 && formData.category) {
            const selectedCat = categories.find(cat => cat._id === formData.category)
            if (selectedCat) {
                setSelectedCategory(selectedCat)
            }
        }
    }, [categories, formData.category])

    // Auto-save draft to localStorage whenever formData changes
    useEffect(() => {
        if (draftSaveTimeoutRef.current) {
            clearTimeout(draftSaveTimeoutRef.current)
        }

        setDraftSaveStatus('saving')

        // Save immediately for critical data, then debounce for performance
        const saveDraft = () => {
            try {
                localStorage.setItem('gig_draft_form', JSON.stringify(formData))
                setDraftSaveStatus('saved')
                setHasDraft(true)

                const clearSaveIndicator = setTimeout(() => {
                    setDraftSaveStatus('unsaved')
                }, 3000)

                return () => clearTimeout(clearSaveIndicator)
            } catch (error) {
                console.error('Error saving draft:', error)
                setDraftSaveStatus('unsaved')
            }
        }

        // Save immediately if this is a significant change (title, description, category)
        const isSignificantChange = formData.title || formData.description || formData.category
        if (isSignificantChange) {
            saveDraft()
        } else {
            // Debounce for minor changes
            draftSaveTimeoutRef.current = setTimeout(saveDraft, 500)
        }

        return () => {
            if (draftSaveTimeoutRef.current) {
                clearTimeout(draftSaveTimeoutRef.current)
            }
        }
    }, [formData])

    // Check if user is not an employer
    useEffect(() => {
        if (user && user.role !== 'employer') {
            navigate('/dashboard')
        }
    }, [user, navigate])

    // Hydrate monthly access limits for employer dashboards
    useEffect(() => {
        if (!user || user.role !== 'employer') {
            return
        }

        const fetchedAt = accessLimitsFetchedAt ? new Date(accessLimitsFetchedAt).getTime() : 0
        const isStale = Date.now() - fetchedAt > 5 * 60 * 1000 // 5 minutes cache window

        if (!accessLimits || isStale) {
            dispatch(fetchAccessLimits())
        }
    }, [dispatch, user, accessLimits, accessLimitsFetchedAt])

    // Fetch categories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setLoadingCategories(true)
                const response = await gigCategoryApi.getCategories()
                // @ts-expect-error - Intentional nested data structure for API response
                setCategories(response.data.data?.categories || [])
            } catch (error) {
                console.error('Error fetching categories:', error)
                showError('Failed to load categories. Please try again.')
            } finally {
                setLoadingCategories(false)
            }
        }

        fetchCategories()
    }, [showError])

    // Check access limits using real-time subscription gatekeeper
    useEffect(() => {
        let isMounted = true

        const checkAccessLimits = async () => {
            if (!user || user.role !== 'employer') {
                setGigLimitStatus(null)
                return
            }

            setIsCheckingAccess(true)

            try {
                const limitStatus = await accessLimitService.canCreateGig()
                if (isMounted) {
                    setGigLimitStatus(limitStatus)
                }
            } catch (error) {
                console.error('Error checking access limits:', error)
                if (isMounted) {
                    setGigLimitStatus({
                        hasAccess: true,
                        isUnlimited: false,
                        requiresUpgrade: false,
                        message: 'Unable to determine gig posting limits right now. Please try again later.'
                    })
                }
            } finally {
                if (isMounted) {
                    setIsCheckingAccess(false)
                }
            }
        }

        checkAccessLimits()

        return () => {
            isMounted = false
        }
    }, [user, accessLimitsFetchedAt])

    // Build category options for dropdown
    const categoryOptions = useMemo<SelectOption[]>(() => {
        return categories.map(cat => ({
            value: cat._id,
            label: cat.name
        }))
    }, [categories])

    // Build subcategory options for dropdown
    const subcategoryOptions = useMemo<SelectOption[]>(() => {
        if (!selectedCategory || selectedCategory.subcategories.length === 0) {
            return []
        }
        return selectedCategory.subcategories.map(sub => ({
            value: sub,
            label: sub
        }))
    }, [selectedCategory])

    // Build service type options for dropdown
    const serviceTypeOptions = useMemo<SelectOption[]>(() => {
        return SERVICE_TYPES.map(type => ({
            value: type.value,
            label: `${type.icon} ${type.label}`
        }))
    }, [])

    // Build duration options for dropdown
    const durationOptions = useMemo<SelectOption[]>(() => [
        { value: '1-3 days', label: '1-3 days' },
        { value: '1 week', label: '1 week' },
        { value: '2-4 weeks', label: '2-4 weeks' },
        { value: '1-3 months', label: '1-3 months' },
        { value: '3+ months', label: '3+ months' }
    ], [])

    // Build urgency options for dropdown
    const urgencyOptions = useMemo<SelectOption[]>(() => [
        { value: 'low', label: 'Low - Flexible timeline' },
        { value: 'medium', label: 'Medium - Standard timeline' },
        { value: 'high', label: 'High - Urgent' }
    ], [])

    // Build experience level options for dropdown
    const experienceOptions = useMemo<SelectOption[]>(() => [
        { value: 'entry', label: 'Entry Level' },
        { value: 'intermediate', label: 'Intermediate' },
        { value: 'expert', label: 'Expert' }
    ], [])

    // Build payment type options for dropdown
    const paymentTypeOptions = useMemo<SelectOption[]>(() => [
        { value: 'fixed', label: 'Fixed Price - Pay a set amount for the entire project' },
        { value: 'hourly', label: 'Hourly Rate - Pay per hour of work' }
    ], [])

    const handleResendVerification = async () => {
        setIsResending(true)
        try {
            await dispatch(resendVerification()).unwrap()
        } catch (error) {
            // Error is handled by the slice
        } finally {
            setIsResending(false)
        }
    }

    const handleClearDraft = () => {
        if (window.confirm('Are you sure you want to clear the draft? This action cannot be undone.')) {
            try {
                localStorage.removeItem('gig_draft_form')
                setFormData({
                    title: '',
                    description: '',
                    category: '',
                    categorySlug: '',
                    subcategory: '',
                    location: {
                        location: '',
                        serviceType: 'onsite' as 'remote' | 'onsite' | 'hybrid'
                    },
                    payment: {
                        amount: '',
                        type: 'fixed' as 'fixed' | 'hourly',
                        currency: userCurrency
                    },
                    duration: {
                        estimated: '',
                        isFlexible: true
                    },
                    requirements: [] as string[],
                    tags: [] as string[],
                    urgency: 'medium' as 'low' | 'medium' | 'high',
                    experience: 'intermediate' as 'entry' | 'intermediate' | 'expert',
                    images: [] as string[],
                    requirementsText: '',
                    requireSubmission: true
                })
                setSelectedCategory(null)
                setHasDraft(false)
                setDraftSaveStatus('unsaved')
                showSuccess('Draft cleared successfully')
            } catch (error) {
                console.error('Error clearing draft:', error)
                showError('Failed to clear draft')
            }
        }
    }

    const handleRefreshData = async () => {
        setIsRefreshing(true)
        try {
            // Refresh access limits
            await dispatch(fetchAccessLimits()).unwrap()

            // Refresh categories
            const response = await gigCategoryApi.getCategories()
            // @ts-expect-error - Intentional nested data structure for API response
            setCategories(response.data.data?.categories || [])

            // Refresh access limit status
            if (user && user.role === 'employer') {
                const limitStatus = await accessLimitService.canCreateGig()
                setGigLimitStatus(limitStatus)
            }

            showSuccess('Data refreshed successfully')
        } catch (error) {
            console.error('Error refreshing data:', error)
            showError('Failed to refresh data')
        } finally {
            setIsRefreshing(false)
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target

        // Handle category selection
        if (name === 'category') {
            const selectedCat = categories?.find(cat => cat._id === value)
            setSelectedCategory(selectedCat || null)
            setFormData(prev => ({
                ...prev,
                category: value,
                categorySlug: selectedCat?.slug || '',
                subcategory: '' // Reset subcategory when category changes
            }))
            return
        }

        // Handle nested object updates
        if (name.includes('.')) {
            const [parent, child] = name.split('.')
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    // @ts-ignore - Spread types may only be created from object types
                    ...prev[parent as keyof typeof prev],
                    [child]: type === 'number' ? parseFloat(value) || 0 : value
                }
            }))
        } else {
            setFormData(prev => ({ ...prev, [name]: value }))
        }
    }

    // Location input handling - simple text input, no validation
    const handleLocationInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target

        // Update form data
        setFormData(prev => ({
            ...prev,
            location: {
                ...prev.location,
                location: value
            }
        }))

        // Clear previous validation error
        setLocationValidationError('')
    }

    const handleServiceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = e.target as any

        setFormData(prev => ({
            ...prev,
            location: {
                ...prev.location,
                serviceType: value
            }
        }))
    }

    // SelectDropdown handlers
    const handleCategoryChange = (value: string) => {
        const selectedCat = categories?.find(cat => cat._id === value)
        setSelectedCategory(selectedCat || null)
        setFormData(prev => ({
            ...prev,
            category: value,
            categorySlug: selectedCat?.slug || '',
            subcategory: '' // Reset subcategory when category changes
        }))
    }

    const handleSubcategoryChange = (value: string) => {
        setFormData(prev => ({
            ...prev,
            subcategory: value
        }))
    }

    const handleServiceTypeDropdownChange = (value: string) => {
        setFormData(prev => ({
            ...prev,
            location: {
                ...prev.location,
                serviceType: value as 'remote' | 'onsite' | 'hybrid'
            }
        }))
    }

    const handleDurationChange = (value: string) => {
        setFormData(prev => ({
            ...prev,
            duration: {
                ...prev.duration,
                estimated: value
            }
        }))
    }

    const handleUrgencyChange = (value: string) => {
        setFormData(prev => ({
            ...prev,
            urgency: value as 'low' | 'medium' | 'high'
        }))
    }

    const handleExperienceChange = (value: string) => {
        setFormData(prev => ({
            ...prev,
            experience: value as 'entry' | 'intermediate' | 'expert'
        }))
    }

    const handlePaymentTypeChange = (value: string) => {
        setFormData(prev => ({
            ...prev,
            payment: {
                ...prev.payment,
                type: value as 'fixed' | 'hourly'
            }
        }))
    }

    const handleImagesChange = (imageIds: string[]) => {
        setFormData(prev => ({ ...prev, images: imageIds }))
    }

    const handleImageUploadError = (error: string) => {
        showError(error)
    }

    const validateLocation = (location: string): boolean => {
        if (!location.trim()) {
            setLocationValidationError('Location is required')
            return false
        }

        setLocationValidationError('')
        return true
    }

    const isFormDisabled = isCheckingAccess || !gigLimitStatus?.hasAccess

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        // Validate all required fields
        if (!formData.title.trim()) {
            showError('Please enter a gig title')
            return
        }

        if (!formData.description.trim()) {
            showError('Please enter a description')
            return
        }

        if (!formData.category) {
            showError('Please select a category')
            return
        }

        if (!formData.subcategory) {
            showError('Please select a subcategory')
            return
        }

        if (!formData.payment.amount) {
            showError('Please enter a budget')
            return
        }

        if (!formData.duration.estimated) {
            showError('Please select a duration')
            return
        }

        // Check access limits before proceeding
        if (!gigLimitStatus?.hasAccess) {
            showError(gigLimitStatus?.message || 'You have reached your gig posting limit')
            return
        }

        // Validate location before proceeding
        if (!validateLocation(formData.location.location)) {
            setIsSubmitting(false)
            return
        }

        setIsSubmitting(true)

        try {
            // Process requirements from textarea
            const processedRequirements = formData.requirementsText
                .split('\n')
                .map(req => req.trim())
                .filter(req => req.length > 0)

            // Prepare gig data for API
            const gigData = {
                title: formData.title,
                description: formData.description,
                category: formData.category,
                subcategory: formData.subcategory || formData.category,
                location: {
                    location: formData.location.location,
                    serviceType: formData.location.serviceType
                },
                payment: {
                    amount: parseFloat(formData.payment.amount),
                    type: formData.payment.type,
                    currency: formData.payment.currency
                },
                duration: {
                    estimated: formData.duration.estimated,
                    flexible: formData.duration.isFlexible
                },
                requirements: processedRequirements,
                tags: formData.tags,
                urgency: formData.urgency,
                experience: formData.experience,
                images: formData.images,
                requireSubmission: formData.requireSubmission,
                featured: false,
                status: 'active' as const
            }



            // @ts-ignore - Type mismatch for gigData
            const response = await gigApi.createGig(gigData)

            console.log('API Response:', response)

            // @ts-expect-error - Intentional nested data structure for API response
            if (response.data?.data?.gig) {
                // @ts-expect-error - Intentional nested data structure for API response
                const gigId = response.data.data.gig._id
                console.log('Navigating to gig:', gigId)
                showSuccess('Gig created successfully! Your gig is now pending admin approval and will be publicly visible once approved.')

                try {
                    localStorage.removeItem('gig_draft_form')
                } catch (error) {
                    console.error('Error clearing draft after submission:', error)
                }

                navigate('/dashboard/my-gigs', { replace: true })
            } else {
                console.error('No gig data in response:', response)
                showError('Gig created but unable to redirect. Please check your gigs.')
            }
        } catch (error: any) {
            console.error('Failed to create gig:', error)

            // Check if this is an access limit error
            if (error.accessLimitError?.isAccessLimitError) {
                showError(error.accessLimitError.message || 'You have reached your gig posting limit')
                // Refresh access limit status
                try {
                    const updatedLimit = await accessLimitService.canCreateGig()
                    setGigLimitStatus(updatedLimit)
                } catch (limitError) {
                    console.error('Failed to refresh access limit:', limitError)
                }
            } else {
                const errorMessage = error?.response?.data?.error?.message ||
                    error?.response?.data?.message ||
                    'Failed to create gig. Please try again.'
                showError(errorMessage)
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    // Show verification required message if email is not verified
    if (!isEmailVerified && !user?.emailVerified) {
        return (
            <>
                <Helmet>
                    <title>Create Gig - SideGO</title>
                    <meta name="description" content="Post a new gig opportunity to attract talented workers." />
                </Helmet>

                <div className="max-w-2xl mx-auto">
                    <div className="text-center mb-8">
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                            Create a New Gig
                        </h1>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                            Post your opportunity to connect with talented workers
                        </p>
                    </div>

                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-8 text-center">
                        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 dark:bg-yellow-900/40 mb-6">
                            <FiMail className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                        </div>

                        <h2 className="text-xl font-semibold text-yellow-800 dark:text-yellow-200 mb-4">
                            Email Verification Required
                        </h2>

                        <p className="text-yellow-700 dark:text-yellow-300 mb-6">
                            To post gigs and hire workers on SideGO, you need to verify your email address first.
                            This helps us maintain a trusted community of employers and workers.
                        </p>

                        <div className="space-y-4">
                            <button
                                onClick={handleResendVerification}
                                disabled={isResending || isLoading}
                                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isResending ? (
                                    <>
                                        <FiRefreshCw className="h-5 w-5 mr-2 animate-spin" />
                                        Sending verification email...
                                    </>
                                ) : (
                                    <>
                                        <FiMail className="h-5 w-5 mr-2" />
                                        Send verification email
                                    </>
                                )}
                            </button>

                            <p className="text-sm text-yellow-600 dark:text-yellow-400">
                                <FiAlertCircle className="inline h-4 w-4 mr-1" />
                                Check your spam folder if you don't see the email in your inbox
                            </p>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            <Helmet>
                <title>Create Gig - SideGO</title>
                <meta name="description" content="Post a new gig opportunity to attract talented workers." />
            </Helmet>

            <div className="max-w-4xl mx-auto">
                <div className="flex items-center justify-between mb-8">
                    <div className="text-center flex-1">
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                            Create a New Gig
                        </h1>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                            Post your opportunity to connect with talented workers
                        </p>
                    </div>
                    <div className="flex-shrink-0 ml-4">
                        <button
                            onClick={handleRefreshData}
                            disabled={isRefreshing}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Refresh data"
                        >
                            <FiRefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                            <span className="ml-2 hidden sm:inline">Refresh</span>
                        </button>
                    </div>
                </div>

                {hasDraft && (
                    <div className="mt-4 flex items-center justify-center gap-3 text-sm">
                        <div className="flex items-center gap-2">
                            {draftSaveStatus === 'saving' && (
                                <>
                                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                                    <span className="text-blue-600 dark:text-blue-400">Saving draft...</span>
                                </>
                            )}
                            {draftSaveStatus === 'saved' && (
                                <>
                                    <FiCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
                                    <span className="text-green-600 dark:text-green-400">Draft saved</span>
                                </>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={handleClearDraft}
                            className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 flex items-center gap-1"
                        >
                            <FiTrash2 className="w-4 h-4" />
                            Clear Draft
                        </button>
                    </div>
                )}

                {/* Access Limit & Subscription Banner */}
                {!isCheckingAccess && user && gigLimitStatus && !gigLimitStatus.isUnlimited && gigLimitStatus.remaining !== undefined && gigLimitStatus.remaining <= 10 && (
                    <VerificationBanner
                        user={user}
                        accessLimit={gigLimitStatus}
                        type="gig"
                        variant={gigLimitStatus.hasAccess ? 'compact' : 'full'}
                        className="mb-6"
                        onVerificationClick={() => navigate('/dashboard/verification')}
                    />
                )}

                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6">
                    <form onSubmit={handleSubmit} noValidate className="space-y-6">
                        {/* Title */}
                        <div>
                            <label htmlFor="title" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                                Gig Title *
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FiTag className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    id="title"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    disabled={isFormDisabled}
                                    className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm md:text-base ${isFormDisabled ? 'border-gray-300 dark:border-gray-600 opacity-50 cursor-not-allowed' : 'border-gray-300 dark:border-gray-600'}`}
                                    placeholder="e.g. Need a logo design for my restaurant"
                                />
                            </div>
                        </div>

                        {/* Description */}
                        <div>
                            <label htmlFor="description" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                                Description *
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows={4}
                                disabled={isFormDisabled}
                                className={`block w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm md:text-base ${isFormDisabled ? 'border-gray-300 dark:border-gray-600 opacity-50 cursor-not-allowed' : 'border-gray-300 dark:border-gray-600'}`}
                                placeholder="Describe what you need in detail..."
                            />
                        </div>

                        {/* Category and Budget */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <SelectDropdown
                                    label="Category *"
                                    value={formData.category}
                                    onChange={handleCategoryChange}
                                    options={categoryOptions}
                                    placeholder={loadingCategories ? 'Loading categories...' : 'Select category'}
                                    disabled={loadingCategories || isFormDisabled}
                                    className="w-full"
                                />
                            </div>

                            <div>
                                <SelectDropdown
                                    label="Subcategory *"
                                    value={formData.subcategory}
                                    onChange={handleSubcategoryChange}
                                    options={subcategoryOptions}
                                    placeholder={
                                        !selectedCategory
                                            ? 'Select a category first'
                                            : selectedCategory.subcategories.length === 0
                                                ? 'No subcategories available'
                                                : 'Select subcategory'
                                    }
                                    disabled={!selectedCategory || selectedCategory.subcategories.length === 0 || isFormDisabled}
                                    className="w-full"
                                />
                            </div>
                        </div>

                        {/* Budget and Payment Type */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="payment.amount" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                                    Budget ({userCurrency}) *
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-gray-400 font-semibold text-lg">{currencySymbol}</span>
                                    </div>
                                    <input
                                        type="number"
                                        id="payment.amount"
                                        name="payment.amount"
                                        value={formData.payment.amount}
                                        onChange={handleChange}
                                        disabled={isFormDisabled}
                                        min="1"
                                        step="0.01"
                                        className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm md:text-base ${isFormDisabled ? 'border-gray-300 dark:border-gray-600 opacity-50 cursor-not-allowed' : 'border-gray-300 dark:border-gray-600'}`}
                                        placeholder="0"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Location and Duration */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="location.location" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                                    Location *
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FiMapPin className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        id="location.location"
                                        name="location.location"
                                        value={formData.location.location}
                                        onChange={handleLocationInputChange}
                                        disabled={isFormDisabled}
                                        className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm md:text-base ${isFormDisabled ? 'opacity-50 cursor-not-allowed' : ''} ${locationValidationError
                                            ? 'border-red-500 dark:border-red-500'
                                            : 'border-gray-300 dark:border-gray-600'
                                            }`}
                                        placeholder="Enter your location (e.g. New York, London, Global)"
                                        autoComplete="off"
                                    />
                                </div>

                                {/* Location Validation Error */}
                                {locationValidationError && (
                                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                                        {locationValidationError}
                                    </p>
                                )}
                            </div>

                            <div>
                                <SelectDropdown
                                    label="Service Type *"
                                    value={formData.location.serviceType}
                                    onChange={handleServiceTypeDropdownChange}
                                    options={serviceTypeOptions}
                                    placeholder="Select service type"
                                    disabled={isFormDisabled}
                                    className="w-full"
                                />
                                <p className="text-sm text-gray-500 mt-1">
                                    {SERVICE_TYPES.find(t => t.value === formData.location.serviceType)?.description}
                                </p>
                            </div>

                            <div>
                                <SelectDropdown
                                    label="Duration *"
                                    value={formData.duration.estimated}
                                    onChange={handleDurationChange}
                                    options={durationOptions}
                                    placeholder="Select duration"
                                    disabled={isFormDisabled}
                                    className="w-full"
                                />
                            </div>
                        </div>

                        {/* Require Submission Toggle */}
                        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                                        Require Work Submission
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                        {formData.requireSubmission
                                            ? "Workers must submit deliverables online before payment is released"
                                            : "On-site work - no online submission required, payment released after completion"
                                        }
                                    </p>
                                </div>
                                <div className="ml-4">
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, requireSubmission: !prev.requireSubmission }))}
                                        disabled={isFormDisabled}
                                        aria-label={formData.requireSubmission ? 'Disable work submission requirement' : 'Enable work submission requirement'}
                                        title={formData.requireSubmission ? 'Submission required' : 'Submission not required'}
                                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                                            formData.requireSubmission
                                                ? 'bg-primary-600'
                                                : 'bg-gray-200 dark:bg-gray-600'
                                        } ${isFormDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        <span
                                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                formData.requireSubmission ? 'translate-x-5' : 'translate-x-0'
                                            }`}
                                        />
                                    </button>
                                </div>
                            </div>
                            <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                                <strong>ON:</strong> Best for remote work, design, programming, writing<br />
                                <strong>OFF:</strong> Best for cleaning, repairs, in-person services
                            </div>
                        </div>

                        {/* Requirements */}
                        <div>
                            <label htmlFor="requirementsText" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                                Requirements & Skills
                            </label>
                            <textarea
                                id="requirementsText"
                                name="requirementsText"
                                value={formData.requirementsText}
                                onChange={handleChange}
                                rows={3}
                                disabled={isFormDisabled}
                                className={`block w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm md:text-base ${isFormDisabled ? 'border-gray-300 dark:border-gray-600 opacity-50 cursor-not-allowed' : 'border-gray-300 dark:border-gray-600'}`}
                                placeholder="Enter each requirement on a new line:&#10;• Minimum 2 years experience with React&#10;• Portfolio of previous work&#10;• Excellent communication skills"
                            />
                            <p className="text-sm text-gray-500 mt-1">List each requirement on a separate line</p>
                        </div>

                        {/* Additional Options */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <SelectDropdown
                                    label="Urgency Level"
                                    value={formData.urgency}
                                    onChange={handleUrgencyChange}
                                    options={urgencyOptions}
                                    placeholder="Select urgency level"
                                    disabled={isFormDisabled}
                                    className="w-full"
                                />
                            </div>

                            <div>
                                <SelectDropdown
                                    label="Experience Level"
                                    value={formData.experience}
                                    onChange={handleExperienceChange}
                                    options={experienceOptions}
                                    placeholder="Select experience level"
                                    disabled={isFormDisabled}
                                    className="w-full"
                                />
                            </div>
                        </div>

                        {/* Payment Type */}
                        <div>
                            <SelectDropdown
                                label="Payment Type"
                                value={formData.payment.type}
                                onChange={handlePaymentTypeChange}
                                options={paymentTypeOptions}
                                placeholder="Select payment type"
                                disabled={isFormDisabled}
                                className="w-full"
                            />
                        </div>

                        {/* Images */}
                        <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                                <FiImage className="inline w-4 h-4 mr-2" />
                                Gig Images (Optional)
                            </label>
                            <GigImageUpload
                                images={formData.images}
                                onImagesChange={handleImagesChange}
                                onUploadError={handleImageUploadError}
                                maxImages={3}
                                disabled={isSubmitting || isFormDisabled}
                                onFullscreenImage={setFullscreenImage}
                            />
                            <p className="text-sm text-gray-500 mt-2">
                                Add up to 3 images to showcase your work, examples, or the services you provide. Images help attract more clients and give them a better understanding of what you offer. Only JPEG, JPG, and PNG files are allowed.
                            </p>
                        </div>

                        {/* Submit Button */}
                        {/* Access Limit Warning */}
                        {gigLimitStatus && !gigLimitStatus.hasAccess && (
                            <VerificationBanner
                                user={user!}
                                accessLimit={gigLimitStatus}
                                type="gig"
                                variant="inline"
                                className="mb-4"
                                onVerificationClick={() => navigate('/dashboard/verification')}
                            />
                        )}

                        <div className="flex justify-end pt-6 border-t border-gray-200 dark:border-gray-700">
                            <button
                                type="submit"
                                disabled={isSubmitting || !gigLimitStatus?.hasAccess || isCheckingAccess}
                                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? (
                                    <LoadingSpinner size="sm" color="white" />
                                ) : isCheckingAccess ? (
                                    'Checking Limits...'
                                ) : !gigLimitStatus?.hasAccess ? (
                                    <>
                                        <FiAlertCircle className="h-5 w-5 mr-2" />
                                        Limit Reached - Upgrade Plan
                                    </>
                                ) : (
                                    <>
                                        <FiPlus className="h-5 w-5 mr-2" />
                                        Post Gig
                                        {gigLimitStatus && !gigLimitStatus.isUnlimited && gigLimitStatus.remaining !== undefined && gigLimitStatus.remaining <= 10 && (
                                            <span className="ml-2 text-xs opacity-80">
                                                ({gigLimitStatus.remaining} remaining)
                                            </span>
                                        )}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Fullscreen Image Modal */}
            {fullscreenImage && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90"
                    onClick={() => setFullscreenImage(null)}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            setFullscreenImage(null)
                        }
                    }}
                    tabIndex={-1}
                >
                    <div className="relative max-w-4xl max-h-screen p-4" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={fullscreenImage}
                            alt="Full size preview"
                            className="max-w-full max-h-full object-contain"
                        />
                        <button
                            onClick={(e) => {
                                e.stopPropagation()
                                setFullscreenImage(null)
                            }}
                            className="absolute top-4 right-4 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
                            title="Close"
                        >
                            <FiX className="w-6 h-6" />
                        </button>
                    </div>
                </div>
            )}
        </>
    )
}

export default CreateGigPage